import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { useModalContext } from '@providers/ModalProvider';
import { CreateSmsCountryBodyDto, SmsCountriesService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { useState } from 'react';

import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

type CountryStatus = 'ALLOW_ALL' | 'ALLOW_WHITELIST';

type StatusOption = {
  label: string;
  value: CountryStatus;
};

export const CreateNewSMSCountry = ({ onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [country, setCountry] = useState({
    code: '',
    prefix: '',
    status: 'ALLOW_ALL' as CreateSmsCountryBodyDto['status'],
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      call(() =>
        SmsCountriesService.create({
          requestBody: {
            code: country.code,
            prefix: country.prefix,
            status: country.status,
          },
        }),
      ),
    onSuccess: () => {
      closeModal();
      onSuccess();
    },
    onError: notifyApiCallError,
  });

  const handleSubmit = () => submitMutation.mutate();

  const statusOptions: StatusOption[] = [
    { label: 'Allow all mobile numbers', value: 'ALLOW_ALL' as CreateSmsCountryBodyDto['status'] },
    { label: 'Allow only whitelisted mobile number', value: 'ALLOW_WHITELIST' as CreateSmsCountryBodyDto['status'] },
  ];

  return (
    <ModalWrapper width="370px">
      {submitMutation.isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create new SMS country rule</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">ISO Country code (2 letters)</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCountry({ ...country, code: e.target.value })}
            label="Code"
            placeholder='e.g. "US"'
            value={country.code}
          />
        </div>

        <div className="input-wrapper">
          <div className="label">International dialing prefix</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCountry({ ...country, prefix: e.target.value })}
            label="Prefix"
            placeholder='e.g. "+1"'
            value={country.prefix}
          />
        </div>

        <div className="input-wrapper">
          <div className="label">Status</div>
          <Select
            value={country.status}
            options={statusOptions}
            onChange={(_, value) => setCountry({ ...country, status: value as CreateSmsCountryBodyDto['status'] })}
            label="Status"
            required
          />
        </div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={handleSubmit} disabled={submitMutation.isPending}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
